import React, { useMemo, useEffect, useCallback, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import OBCSpinner from "../util/OBC";
import InfinitePatrols from "./InfinitePatrols";
import moment from "moment";
import _ from "lodash";
import { calculateRoutePercentageCovered, calculateTotalPatrolChainage } from "../util/PlaylistUtils";
import { Button, Modal, Progress, Select } from "antd";
import AutoSizer from "react-virtualized-auto-sizer";
import { useHistory } from "react-router-dom";
import RoutePatrolSvg from "./RoutePatrolSvg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faCalendar, faTrain, faUndo, faArchive, faUser, faExternalLinkAlt } from "@fortawesome/pro-solid-svg-icons";
import { VariableSizeList } from "react-window";
import { Tooltip } from "antd";
import { archivePatrolPlan, assignPatrol } from "../../redux/actions/index";
import Highlighter from "react-highlight-words";

const patrolsSelector = (state) => state.schemaInterface.patrols;
const patrolsLoadedSelector = (state) => state.schemaInterface.patrolsLoaded;
const reviewerPatrolsSelector = (state) => state.schemaInterface.reviewerPatrols;
const reviewerPatrolsLoadedSelector = (state) => state.schemaInterface.reviewerPatrolsLoaded;
const isSupervisorSelector = (state) => state.userDetails.userConfig.is_patrol_supervisor;
const patrolPlansSelector = (state) => state.schemaInterface.patrolPlans;
const patrolUsersSelector = (state) => state.schemaInterface.patrolUsers.users;
const patrolShiftsSelector = (state) => state.schemaInterface.patrolShifts;

const FULL_DATE_FORMAT = "DD MMM YYYY";

const PATROL_STATUS_REVIEWED = 3;
const EXPANDED_ROW_HEIGHT = 80;
const EXPANDED_ROW_MAX_HEIGHT = 800;

const GUTTER_SIZE = 8;
const SESSION_ITEM_HEIGHT = 116;
const RECENT_COVERAGE_PERIOD_IN_DAYS = 60;

const PlanRecentCoverage = ({ plan }) => {
    const patrolShifts = useSelector(patrolShiftsSelector);

    const recentCoverageDays = Array.from({ length: RECENT_COVERAGE_PERIOD_IN_DAYS }, (_, index) =>
        moment()
            .subtract(RECENT_COVERAGE_PERIOD_IN_DAYS - index, "days")
            .format("YYYY-MM-DD"),
    );

    return (
        <div
            style={{
                padding: "5px 10px",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                alignContent: "center",
                justifyContent: "flex-start",
                marginLeft: "240px",
            }}>
            <div style={{ marginTop: "6px", marginRight: "6px" }}>Coverage History:</div>
            <div
                className="PatrolPlanFooter"
                style={{ margin: 0 }}>
                {recentCoverageDays.map((date, index) => {
                    const formattedDate = moment(date).format("D MMM YYYY");
                    const shiftDate = moment(date).format("DD-MM-YYYY");
                    const shiftNames = _.get(patrolShifts, [shiftDate], []);
                    const planShiftNumbers = _.get(plan, ["shift_numbers"], []);
                    const matchingShifts = _.intersection(shiftNames, planShiftNumbers);

                    const coverage = _.get(plan, ["recent_coverage", date], null);

                    let dot;
                    if (coverage) {
                        let dotColourClass = "yellow";

                        if (coverage >= plan.coverage_threshold) {
                            dotColourClass = "green";
                        }

                        dot = (
                            <Tooltip title={`${formattedDate}: ${coverage}% covered`}>
                                <span className={`dot ${dotColourClass}`}></span>
                            </Tooltip>
                        );
                    } else {
                        dot = (
                            <Tooltip title={`${formattedDate}: Not covered`}>
                                <span className="dot gray"></span>
                            </Tooltip>
                        );
                    }

                    return (
                        <div
                            key={index}
                            className="dot-container">
                            {matchingShifts.length > 0 ? (
                                <Tooltip title={matchingShifts.join(", ")}>
                                    <span className="dot shift-dot"></span>
                                </Tooltip>
                            ) : (
                                <span className="dot-spacer"></span>
                            )}
                            {dot}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const AdminPatrolListItem = ({ plan, style, expanded, onToggle, openConfirmModal, openReassignModal, searchQuery }) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const [patrolsSortBy, setPatrolsSortBy] = useState("due_ts");

    const patrolUsers = useSelector(patrolUsersSelector);

    const navigate = () => {
        history.push(`/patrol-planner/${plan.id}`, {
            planID: plan.id,
        });
    };

    const handleOnClick = () => {
        onToggle();
    };

    const sortedPatrols = useMemo(() => {
        const sorted = _.orderBy(plan.patrols, [patrolsSortBy], [["due_ts", "calculatedCoverage"].includes(patrolsSortBy) ? "desc" : "asc"]);
        return sorted;
    }, [plan.patrols, patrolsSortBy]);

    const assignUserOptions = useMemo(() => {
        return _.map(patrolUsers, (user) => {
            return (
                <Select.Option
                    key={user.id}
                    value={user.id}
                    title={`${user.name} (${user.email})`}>
                    {user.name}
                </Select.Option>
            );
        });
    }, [patrolUsers]);

    const onAssignChange = (patrol, userID) => {
        if (!patrol.user_id) {
            openConfirmModal(patrol.id, userID);
        } else {
            openReassignModal(patrol.id, userID);
        }
    };

    return (
        <div
            onClick={handleOnClick}
            style={{ ...style, justifyContent: "start", width: "calc(100% - 5px)" }}
            className={`PatrolPlanContainer ${expanded ? "expanded" : ""}`}>
            <div
                className="PatrolPlan"
                style={{ position: "relative", flexDirection: "column", paddingBottom: "5px" }}>
                <div style={{ position: "absolute", top: "-8px", left: "-10px", fontSize: "11px", color: "#ababab" }}>#{_.get(plan, "id", "Unknown")}</div>
                <div className="PatrolPlanTop">
                    <div className="PatrolPlan__Svg">
                        <RoutePatrolSvg
                            planData={plan}
                            preview
                        />
                    </div>
                    <div
                        className="PatrolPlan__Info"
                        style={{ flex: 1, padding: "0px 10px" }}>
                        <div className="Title">
                            <Highlighter
                                highlightClassName="ObcTextHighlight"
                                searchWords={String(searchQuery).split(" ")}
                                autoEscape={true}
                                textToHighlight={_.get(plan, "name", "Unnamed Plan")}
                            />
                        </div>
                        <div
                            className="data-collection"
                            style={{ marginBottom: 0 }}>
                            {_.get(plan, "last_covered_percentage", 0)}% covered{" "}
                            {_.get(plan, "last_covered_ts") ? moment.unix(_.get(plan, "last_covered_ts") / 1000).fromNow() : ""}{" "}
                            {_.get(plan, "last_covered_device") ? `by ${_.get(plan, "last_covered_device")}` : ""}
                        </div>
                        <Progress
                            percent={plan.last_covered_percentage || 0}
                            showInfo={false}
                            trailColor={"#26293a"}
                        />
                        <div style={{ textAlign: "left" }}>
                            Patrols count:{" "}
                            <span style={{ fontWeight: "bold", backgroundColor: "#26293a", padding: "2px 4px", borderRadius: "2px" }}>
                                {sortedPatrols.length}
                            </span>
                        </div>
                    </div>
                    <div
                        className="PatrolPlan__Chevron"
                        style={{ height: "100%", display: "flex", alignItems: "center", padding: "0px 10px", transition: "transform 0.3s ease-in-out" }}>
                        <FontAwesomeIcon
                            icon={faChevronRight}
                            // style={{ rotate: expanded ? "90deg" : "0deg" }}
                        />
                    </div>
                </div>
                {expanded && <PlanRecentCoverage plan={plan} />}
            </div>
            {expanded && (
                <div
                    className="PatrolPlanExpanded"
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                    }}>
                    <div className="PatrolPlanExpandedHeader">
                        <div style={{ fontSize: "15px" }}>Patrols in this plan:</div>
                        {sortedPatrols.length ? (
                            <div className="PatrolPlanExpandedHeaderRight">
                                <div>
                                    <div style={{ fontSize: "10px", textAlign: "left" }}>Sort by:</div>
                                    <Select
                                        value={patrolsSortBy}
                                        style={{ width: 150 }}
                                        disabled={sortedPatrols.length === 1}
                                        onChange={(value) => setPatrolsSortBy(value)}>
                                        <Select.Option value="calculatedCoverage">Completion</Select.Option>
                                        <Select.Option value="due_ts">Scheduled Date</Select.Option>
                                        <Select.Option value="status">Status</Select.Option>
                                        <Select.Option value="name">Name</Select.Option>
                                    </Select>
                                </div>
                                <Button
                                    type="primary"
                                    onClick={() => {
                                        navigate();
                                    }}>
                                    Create New Patrol
                                </Button>
                            </div>
                        ) : null}
                    </div>
                    <div
                        className="PatrolPlanExpandedPatrolsList"
                        style={{ maxHeight: `calc(${EXPANDED_ROW_MAX_HEIGHT - SESSION_ITEM_HEIGHT - 100}px)` }}>
                        {!sortedPatrols.length && (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "5px",
                                    marginTop: "10px",
                                    marginBottom: "10px",
                                }}>
                                <div>No patrols created yet</div>
                                <Button
                                    type="primary"
                                    size="small"
                                    onClick={() => {
                                        navigate();
                                    }}>
                                    Create New Patrol
                                </Button>
                            </div>
                        )}
                        {_.map(sortedPatrols, (patrol, index) => {
                            let due_moment = patrol.due_ts ? moment.unix(patrol.due_ts) : null;
                            let dueDate = null;
                            if (due_moment) {
                                dueDate = due_moment.calendar(moment(), {
                                    sameDay: "[Today]",
                                    nextDay: "[Tomorrow]",
                                    nextWeek: "dddd",
                                    lastDay: "[Yesterday]",
                                    lastWeek: "[Last] dddd",
                                    sameElse: FULL_DATE_FORMAT,
                                });
                            }

                            let status = "Not Started";
                            if (!_.isNil(patrol.status)) {
                                let st = patrol.status;
                                if (st === 1) {
                                    status = "In Progress";
                                } else if (st === 2) {
                                    status = "Awaiting Review";
                                } else if (st === 3) {
                                    status = "Reviewed";
                                }
                            }

                            let collectionDaysDiff = 0;
                            if (patrol.start_timestamp && patrol.end_timestamp) {
                                collectionDaysDiff = moment.unix(patrol.end_timestamp).diff(moment.unix(patrol.start_timestamp), "days") + 1;
                            }
                            const startDate = moment.unix(patrol.start_timestamp).format(FULL_DATE_FORMAT);
                            const endDate = moment.unix(patrol.end_timestamp).format(FULL_DATE_FORMAT);
                            const dayLabel = `${collectionDaysDiff} day${collectionDaysDiff > 1 ? "s" : ""}`;

                            return (
                                <div
                                    key={patrol.id}
                                    className={`PatrolPlanExpandedPatrol ${patrol.archived ? "archived" : ""}`}
                                    style={{
                                        height: `calc(${EXPANDED_ROW_HEIGHT}px - 2px)`,
                                        marginRight: "3px",
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        history.push(`/review-patrol/${patrol.id}`);
                                    }}>
                                    {/* Left info pane */}
                                    <div className="PatrolPlanExpandedPatrolInfo">
                                        <h3 style={{ color: patrol.archived ? "#898989" : "#e8dfff", margin: 0, fontWeight: "bold" }}>{patrol.name}</h3>
                                        <Tooltip
                                            title="Scheduled date of patrol"
                                            placement="right">
                                            <div
                                                className="PatrolPlanExpandedPatrolDueDate"
                                                style={{ color: patrol.archived ? "#757575" : "#fff" }}>
                                                <FontAwesomeIcon icon={faCalendar} />
                                                <span>
                                                    Scheduled DOP: {dueDate ? <strong>{dueDate}</strong> : <em style={{ color: "#a1a1a1" }}>not set</em>}
                                                </span>
                                            </div>
                                        </Tooltip>

                                        {patrol.start_timestamp && patrol.end_timestamp && (
                                            <Tooltip
                                                title="Data collection window"
                                                placement="right">
                                                <div
                                                    className="PatrolPlanExpandedPatrolDataCollection"
                                                    style={{ color: patrol.archived ? "#757575" : "#8f8eaa" }}>
                                                    <FontAwesomeIcon icon={faTrain} />
                                                    {`${startDate} - ${endDate} (${dayLabel})`}
                                                </div>
                                            </Tooltip>
                                        )}
                                    </div>

                                    {/* right info pane */}
                                    <div className="PatrolPlanExpandedPatrolStatus">
                                        {/* archived status */}
                                        {patrol.archived ? (
                                            <div className="PatrolPlanExpandedPatrolStatusText">
                                                <div className={`Status Archived`}>Archived</div>
                                            </div>
                                        ) : null}
                                        {/* Patroller select */}
                                        <div
                                            className="User"
                                            style={{ display: "flex", alignItems: "flex-start", gap: "1px", flexDirection: "column", alignSelf: "flex-start" }}
                                            onClick={(e) => e.stopPropagation()}>
                                            <div style={{ display: "flex", alignItems: "center", gap: "5px", flexDirection: "row" }}>
                                                <FontAwesomeIcon
                                                    icon={faUser}
                                                    style={{ fontSize: "10px" }}
                                                />
                                                <div style={{ fontSize: "11px" }}>Patroller:</div>
                                            </div>
                                            <Select
                                                disabled={patrol.status === 2 || patrol.status === 3 || patrol.archived}
                                                showSearch
                                                style={{ width: 200, fontWeight: patrol.user_name || patrol.user_email ? 600 : 300 }}
                                                value={patrol.user_id ? patrol.user_id : undefined}
                                                placeholder="Select Patroller..."
                                                optionFilterProp="children"
                                                onChange={(val) => {
                                                    onAssignChange(patrol, val);
                                                }}>
                                                {assignUserOptions}
                                            </Select>
                                        </div>
                                        <div className="PatrolPlanExpandedPatrolStatusText">
                                            <div className={`Status ${status}`}>{status}</div>
                                        </div>

                                        {/* Progress */}
                                        <div className="PatrolPlanExpandedPatrolProgress">
                                            <Tooltip title="Total Inspected">
                                                <Progress
                                                    type="circle"
                                                    percent={patrol.calculatedCoverage || 0}
                                                    width={50}
                                                    strokeColor="#6c43df"
                                                    strokeWidth={7}
                                                />
                                            </Tooltip>
                                        </div>

                                        {/* Archive button */}
                                        <div>
                                            <Tooltip title={patrol.archived ? "Unarchive patrol" : "Archive patrol"}>
                                                <FontAwesomeIcon
                                                    style={{ fontSize: 18, color: patrol.archived ? "#4ed654" : "#d05858" }}
                                                    icon={patrol.archived ? faUndo : faArchive}
                                                    className="ClickableIcon"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        Modal.confirm({
                                                            title: patrol.archived ? "Unarchive patrol?" : "Archive patrol?",
                                                            content: patrol.archived
                                                                ? "This will make the patrol visible in the list of active patrols."
                                                                : "This will hide the patrol from the list of active patrols.",
                                                            okText: patrol.archived ? "Unarchive" : "Archive",
                                                            cancelText: "Cancel",
                                                            onOk: () => {
                                                                dispatch(archivePatrolPlan(patrol.id, !patrol.archived));
                                                            },
                                                        });
                                                    }}
                                                />
                                            </Tooltip>
                                        </div>

                                        {/* Open in new tab button */}
                                        <div>
                                            <Tooltip title={"Open patrol in new tab"}>
                                                <FontAwesomeIcon
                                                    className="ClickableIcon"
                                                    style={{ fontSize: 18 }}
                                                    icon={faExternalLinkAlt}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        window.open(`/review-patrol/${patrol.id}`, "_blank", "noopener,noreferrer");
                                                    }}
                                                />
                                            </Tooltip>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {/* <div className="PatrolPlanFooter">{recentRuns}</div> */}
        </div>
    );
};

const PatrolList = ({
    onlyIncomplete,
    showUnusedPatrols,
    searchQuery,
    dateRange,
    sameDay,
    statusFilter,
    archivedPatrolsVisible,
    sortType,
    superUserSort,
    userSelected,
    openDiagramDrawer,
    expandAllRows,
}) => {
    const listRef = useRef();
    const dispatch = useDispatch();

    const routePatrols = useSelector(patrolsSelector);
    const routePatrolsLoaded = useSelector(patrolsLoadedSelector);
    const reviewerPatrols = useSelector(reviewerPatrolsSelector);
    const reviewerPatrolsLoaded = useSelector(reviewerPatrolsLoadedSelector);
    const isSupervisor = useSelector(isSupervisorSelector);
    const patrolPlans = useSelector(patrolPlansSelector);
    const [expandedRows, setExpandedRows] = useState({});

    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [reassignModalOpen, setReassignModalOpen] = useState(false);
    const [currentPatrol, setCurrentPatrol] = useState();
    const [userToAssign, setUserToAssign] = useState();

    // Reset expanded rows when filters change
    useEffect(() => {
        if (listRef.current) {
            listRef.current.resetAfterIndex(0, true);
        }
    }, [statusFilter, userSelected, searchQuery, dateRange, onlyIncomplete, archivedPatrolsVisible]);

    const openConfirmModal = (patrolID, userID) => {
        setCurrentPatrol(patrolID);
        setUserToAssign(userID);
        setConfirmModalOpen(true);
    };

    const openReassignModal = (patrolID, userID) => {
        setCurrentPatrol(patrolID);
        setUserToAssign(userID);
        setReassignModalOpen(true);
    };

    const confirmAssignUser = (notify) => {
        dispatch(assignPatrol(currentPatrol, userToAssign, notify));
        setConfirmModalOpen(false);
        setReassignModalOpen(false);
    };

    const patrols = useMemo(() => {
        if (isSupervisor) {
            return reviewerPatrols;
        } else {
            return routePatrols;
        }
    }, [routePatrols, reviewerPatrols, isSupervisor]);

    const patrolsWithCoverage = useMemo(() => {
        return patrols.map((patrol) => {
            const totalChainage = calculateTotalPatrolChainage(patrol.svg.line);
            const coverage = calculateRoutePercentageCovered(patrol.svg.line, patrol.progress, totalChainage);
            return { ...patrol, calculatedCoverage: coverage };
        });
    }, [patrols]);

    const filteredPatrols = useMemo(() => {
        let retVal = [];

        if (patrolsWithCoverage && patrolsWithCoverage.length) {
            let patrols = patrolsWithCoverage;
            if (!_.isNil(sortType) && !isSupervisor) {
                if (sortType === 0) {
                    //deadline date
                    patrols = _.sortBy(patrols, (patrol) => {
                        let due_ts = patrol.due_ts ? patrol.due_ts : 0;
                        return [due_ts, patrol.name];
                    });
                } else if (sortType === 1) {
                    //status
                    patrols = _.sortBy(patrols, (patrol) => {
                        let due_ts = patrol.due_ts ? patrol.due_ts : 0;
                        let name = _.get(patrol, "name");
                        let status = _.get(patrol, "status");
                        return [status, due_ts, name];
                    });
                }
            }

            retVal = patrols.filter((patrol) => {
                if (onlyIncomplete) {
                    let filtered = patrol.calculatedCoverage === 100;
                    if (filtered) {
                        return false;
                    }
                }

                if (isSupervisor && !showUnusedPatrols) {
                    // unused patrols are both unassigned and past-due
                    let unassigned = !patrol.user_id;
                    let due_ts = patrol.due_ts ? patrol.due_ts : 0;
                    let overdue = due_ts < moment().startOf("day").unix();
                    if (unassigned && overdue) {
                        return false;
                    }
                }

                if (searchQuery && !isSupervisor) {
                    let filtered = !patrol.name.toLowerCase().includes(searchQuery);
                    if (filtered) {
                        return false;
                    }
                }

                if (dateRange.to && dateRange.from) {
                    const start = moment(dateRange.from).startOf("day").unix();
                    const end = moment(dateRange.to).endOf("day").unix();

                    const scheduledDate = new Date(patrol.due_ts * 1000);
                    const scheduledTs = moment(scheduledDate).endOf("day").unix();

                    if (scheduledTs < start || scheduledTs > end) {
                        return false;
                    }
                }

                if (statusFilter === "all_current") {
                    // all_current: anything which is not reviewed already
                    if (patrol.status === PATROL_STATUS_REVIEWED) {
                        return false;
                    }
                } else if (statusFilter === "unassigned") {
                    // unassigned: only show patrols without assigned patroller
                    if (patrol.user_name || patrol.user_email) {
                        return false;
                    }
                } else if (Number.isInteger(statusFilter)) {
                    // specific statuses: not started, in progress, awaiting review, reviewed
                    // we also want to hide unassigned patrols from 'assigned - not started' status
                    if (patrol.status !== statusFilter || (isSupervisor && !patrol.user_email)) {
                        return false;
                    }
                }

                if (!archivedPatrolsVisible) {
                    if (patrol.archived) {
                        return false;
                    }
                }

                if (userSelected) {
                    if (patrol.user_id !== userSelected) {
                        return false;
                    }
                }

                return true;
            });
        }

        return retVal;
    }, [
        patrolsWithCoverage,
        statusFilter,
        searchQuery,
        dateRange,
        onlyIncomplete,
        archivedPatrolsVisible,
        sortType,
        userSelected,
        showUnusedPatrols,
        isSupervisor,
    ]);

    const patrolPlansWithPatrols = useMemo(() => {
        if (!isSupervisor) {
            return null;
        }

        let newPlans = [];

        _.forEach(patrolPlans, (plan) => {
            const planPatrols = _.filter(filteredPatrols, (patrol) => patrol.plan_id === plan.id);
            newPlans.push({ ...plan, patrols: planPatrols });
        });

        return newPlans;
    }, [patrolPlans, filteredPatrols, isSupervisor]);

    const filteredPlansWithPatrols = useMemo(() => {
        if (!patrolPlansWithPatrols) {
            return [];
        }

        return patrolPlansWithPatrols.filter((plan) => {
            if (searchQuery) {
                const planName = plan.name.toLowerCase();
                if (!planName.includes(searchQuery)) {
                    return false;
                }
            }

            if (userSelected > 0) {
                const hasUser = plan.patrols.some((patrol) => patrol.user_id === userSelected);
                if (!hasUser) {
                    return false;
                }
            }

            return true;
        });
    }, [patrolPlansWithPatrols, searchQuery, userSelected]);

    const sortedPatrolPlansWithPatrols = useMemo(() => {
        if (!filteredPlansWithPatrols || !isSupervisor) {
            return [];
        }

        if (superUserSort === "patrols_count") {
            return _.orderBy(filteredPlansWithPatrols, ["patrols.length", "last_covered_ts"], ["desc", "desc"]);
        }

        if (superUserSort === "coverage_percent") {
            return _.orderBy(filteredPlansWithPatrols, ["last_covered_percentage", "last_covered_ts"], ["desc", "desc"]);
        }

        if (superUserSort === "last_covered_time") {
            return _.orderBy(filteredPlansWithPatrols, ["last_covered_ts", "patrols.length"], ["desc", "desc"]);
        }

        if (superUserSort === "alphabetical") {
            return _.orderBy(filteredPlansWithPatrols, ["name"], ["asc"]);
        }

        return _.orderBy(filteredPlansWithPatrols, ["patrols.length", "last_covered_ts"], ["desc", "desc"]);
    }, [filteredPlansWithPatrols, superUserSort, isSupervisor]);

    const getItemSize = useCallback(
        (index) => {
            const plan = sortedPatrolPlansWithPatrols[index];

            let planHeight = SESSION_ITEM_HEIGHT + 100 + plan.patrols.length * EXPANDED_ROW_HEIGHT;

            if (plan.patrols.length === 0) {
                planHeight = SESSION_ITEM_HEIGHT + 100 + 50; // add extra space for "no patrols" message
            }

            const newHeight = expandedRows[plan.id] ? planHeight : SESSION_ITEM_HEIGHT;

            return newHeight > EXPANDED_ROW_MAX_HEIGHT ? EXPANDED_ROW_MAX_HEIGHT : newHeight;
        },
        [expandedRows, sortedPatrolPlansWithPatrols],
    );

    // if expandAllRows true then set all rows to expanded
    useEffect(() => {
        if (expandAllRows) {
            const newExpandedRows = {};
            patrolPlans.forEach((plan) => {
                newExpandedRows[plan.id] = true;
            });
            setExpandedRows(newExpandedRows);
        } else {
            setExpandedRows({});
        }
        if (listRef.current) {
            listRef.current.resetAfterIndex(0, true);
        }
    }, [expandAllRows, patrolPlans]);

    const toggleExpanded = (planId, index) => {
        setExpandedRows((prev) => ({
            ...prev,
            [planId]: !prev[planId],
        }));

        if (listRef.current) {
            listRef.current.resetAfterIndex(index);
        }
    };

    const Row = useCallback(
        ({ index, style }) => {
            const plan = sortedPatrolPlansWithPatrols[index];

            const styles = {
                ...style,
                left: style.left,
                top: index === 0 ? 0 : style.top,
                height: style.height - GUTTER_SIZE,
            };

            return (
                <AdminPatrolListItem
                    style={styles}
                    plan={plan}
                    expanded={!!expandedRows[plan.id]}
                    onToggle={() => toggleExpanded(plan.id, index)}
                    openConfirmModal={openConfirmModal}
                    openReassignModal={openReassignModal}
                    searchQuery={searchQuery}
                />
            );
        },
        [sortedPatrolPlansWithPatrols, expandedRows, searchQuery],
    );

    const assignModal = (
        <Modal
            title="Assign Patrol"
            visible={confirmModalOpen}
            footer={[
                <Button onClick={() => setConfirmModalOpen(false)}>Cancel</Button>,
                <Button
                    onClick={() => confirmAssignUser(false)}
                    type="primary">
                    Assign
                </Button>,
                <Button
                    onClick={() => confirmAssignUser(true)}
                    type="primary">
                    Assign & Notify
                </Button>,
            ]}
            onCancel={() => setConfirmModalOpen(false)}>
            <p>Are you sure you would like to assign this patrol?</p>
        </Modal>
    );

    const reassignModal = (
        <Modal
            title="Reassign Patrol"
            visible={reassignModalOpen}
            footer={[
                <Button onClick={() => setReassignModalOpen(false)}>Cancel</Button>,
                <Button
                    onClick={() => confirmAssignUser(false)}
                    type="primary">
                    Re-Assign
                </Button>,
                <Button
                    onClick={() => confirmAssignUser(true)}
                    type="primary">
                    Re-Assign & Notify
                </Button>,
            ]}
            onCancel={() => setReassignModalOpen(false)}>
            <div>Are you sure you would like to re-assign this patrol?</div>
            <strong style={{ color: "#e55151" }}>All existing progress will be reset</strong>
        </Modal>
    );

    const content = useMemo(() => {
        if (!reviewerPatrolsLoaded && !routePatrolsLoaded) {
            return (
                <div className="CenteredFlexContainer">
                    <OBCSpinner colorScheme={"mono"} />
                </div>
            );
        }
        if ((patrols && !patrols.length) || (filteredPatrols && !filteredPatrols.length)) {
            return (
                <div className="CenteredFlexContainer">
                    <p className="Text">
                        No patrol jobs matching the current filters
                        <br />
                        <Button
                            type="primary"
                            onClick={() => openDiagramDrawer(true)}>
                            View all patrol diagrams
                        </Button>
                    </p>
                </div>
            );
        }

        if (isSupervisor) {
            if (!sortedPatrolPlansWithPatrols || !sortedPatrolPlansWithPatrols.length) {
                return (
                    <div className="CenteredFlexContainer">
                        {assignModal}
                        {reassignModal}
                        <p className="Text">
                            No patrol diagrams have been uploaded yet
                            <br />
                            <Button
                                type="primary"
                                onClick={() => openDiagramDrawer(true)}>
                                Upload a patrol diagram
                            </Button>
                        </p>
                    </div>
                );
            }
            return (
                <div className="PatrolPlanList">
                    {assignModal}
                    {reassignModal}
                    <AutoSizer className="sessionAutosizer">
                        {({ height, width }) => (
                            <VariableSizeList
                                ref={listRef}
                                height={height + GUTTER_SIZE * 2}
                                width={width}
                                itemSize={getItemSize}
                                itemCount={sortedPatrolPlansWithPatrols.length}>
                                {Row}
                            </VariableSizeList>
                        )}
                    </AutoSizer>
                </div>
            );
        }
        return (
            <InfinitePatrols
                groupByDate={sortType === 0}
                patrols={filteredPatrols}
                searchQuery={searchQuery}
            />
        );
    }, [
        reviewerPatrolsLoaded,
        routePatrolsLoaded,
        patrols,
        filteredPatrols,
        isSupervisor,
        sortType,
        searchQuery,
        openDiagramDrawer,
        sortedPatrolPlansWithPatrols,
        Row,
        getItemSize,
        assignModal,
        reassignModal,
    ]);

    return <div className="PatrolList">{content}</div>;
};

export default PatrolList;
